"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

function parseNum(value: string): number | null {
  return parseNumberInput(value);
}

function fmt(n: number, formatNum: NumberFormatter): string {
  const rounded = Math.round(n * 10_000) / 10_000;
  return formatNum(rounded, { maximumFractionDigits: 4 });
}

function changeDirection(
  change: number,
  strings: { increase: string; decrease: string; noChange: string }
): string {
  if (change > 0) {
    return strings.increase;
  }
  if (change < 0) {
    return strings.decrease;
  }
  return strings.noChange;
}

function NumberField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="block font-medium text-foreground text-sm" htmlFor={id}>
        {label}
      </label>
      <input
        className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        id={id}
        onChange={(e) => onChange(e.target.value)}
        step="any"
        type="number"
        value={value}
      />
    </div>
  );
}

function ResultBlock({
  headline,
  working,
}: {
  headline: string;
  working: string;
}) {
  return (
    <div className="space-y-2">
      <p className="font-semibold text-2xl text-foreground">{headline}</p>
      <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm leading-relaxed">
        {working}
      </p>
    </div>
  );
}

function PercentOfCalculator({
  strings,
}: {
  strings: typeof PERCENTAGE_CALCULATOR_STRINGS;
}) {
  const [percent, setPercent] = useState("15");
  const [base, setBase] = useState("80");
  const formatNum = useToolNumber();
  const p = parseNum(percent);
  const b = parseNum(base);
  const valid = p !== null && b !== null;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.whatIsXPercentOfY}
      </h2>
      <div className="flex flex-wrap items-end gap-6">
        <NumberField
          id="pct-of-percent"
          label={strings.percentageXLabel}
          onChange={setPercent}
          value={percent}
        />
        <NumberField
          id="pct-of-base"
          label={strings.numberYLabel}
          onChange={setBase}
          value={base}
        />
      </div>
      {valid && (
        <ResultBlock
          headline={t(strings.headlinePercentOf, {
            percent: fmt(p, formatNum),
            base: fmt(b, formatNum),
            result: fmt((p / 100) * b, formatNum),
          })}
          working={`(${fmt(p, formatNum)} ÷ 100) × ${fmt(b, formatNum)} = ${fmt(p / 100, formatNum)} × ${fmt(b, formatNum)} = ${fmt((p / 100) * b, formatNum)}`}
        />
      )}
      {!valid && (
        <p className="text-muted-foreground text-sm">
          {strings.enterBothNumbers}
        </p>
      )}
    </section>
  );
}

function PercentOfWhatCalculator({
  strings,
}: {
  strings: typeof PERCENTAGE_CALCULATOR_STRINGS;
}) {
  const [part, setPart] = useState("30");
  const [whole, setWhole] = useState("120");
  const formatNum = useToolNumber();
  const x = parseNum(part);
  const y = parseNum(whole);
  const valid = x !== null && y !== null && y !== 0;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.xIsWhatPercentOfY}
      </h2>
      <div className="flex flex-wrap items-end gap-6">
        <NumberField
          id="pct-part"
          label={strings.partXLabel}
          onChange={setPart}
          value={part}
        />
        <NumberField
          id="pct-whole"
          label={strings.wholeYLabel}
          onChange={setWhole}
          value={whole}
        />
      </div>
      {valid && (
        <ResultBlock
          headline={t(strings.headlineIsPercentOf, {
            part: fmt(x, formatNum),
            percent: fmt((x / y) * 100, formatNum),
            whole: fmt(y, formatNum),
          })}
          working={`(${fmt(x, formatNum)} ÷ ${fmt(y, formatNum)}) × 100 = ${fmt(x / y, formatNum)} × 100 = ${fmt((x / y) * 100, formatNum)}%`}
        />
      )}
      {!valid && (
        <p className="text-muted-foreground text-sm">
          {strings.enterBothNumbersWholeNonzero}
        </p>
      )}
    </section>
  );
}

function PercentChangeCalculator({
  strings,
}: {
  strings: typeof PERCENTAGE_CALCULATOR_STRINGS;
}) {
  const [from, setFrom] = useState("50");
  const [to, setTo] = useState("65");
  const formatNum = useToolNumber();
  const a = parseNum(from);
  const b = parseNum(to);
  const valid = a !== null && b !== null && a !== 0;
  const change = valid ? ((b - a) / Math.abs(a)) * 100 : 0;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.percentChangeFromXToY}
      </h2>
      <div className="flex flex-wrap items-end gap-6">
        <NumberField
          id="chg-from"
          label={strings.startingValueXLabel}
          onChange={setFrom}
          value={from}
        />
        <NumberField
          id="chg-to"
          label={strings.endingValueYLabel}
          onChange={setTo}
          value={to}
        />
      </div>
      {valid && (
        <ResultBlock
          headline={`${fmt(Math.abs(change), formatNum)}% ${changeDirection(change, strings)}`}
          working={`((${fmt(b, formatNum)} − ${fmt(a, formatNum)}) ÷ |${fmt(a, formatNum)}|) × 100 = (${fmt(b - a, formatNum)} ÷ ${fmt(Math.abs(a), formatNum)}) × 100 = ${fmt(change, formatNum)}%`}
        />
      )}
      {!valid && (
        <p className="text-muted-foreground text-sm">
          {strings.enterBothValuesStartingNonzero}
        </p>
      )}
    </section>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const PERCENTAGE_CALCULATOR_STRINGS = {
  whatIsXPercentOfY: "What is X% of Y?",
  percentageXLabel: "Percentage (X%)",
  numberYLabel: "Number (Y)",
  enterBothNumbers: "Enter both numbers to see the result.",
  xIsWhatPercentOfY: "X is what percent of Y?",
  partXLabel: "Part (X)",
  wholeYLabel: "Whole (Y)",
  enterBothNumbersWholeNonzero:
    "Enter both numbers (the whole cannot be zero) to see the result.",
  percentChangeFromXToY: "Percent change from X to Y",
  startingValueXLabel: "Starting value (X)",
  endingValueYLabel: "Ending value (Y)",
  enterBothValuesStartingNonzero:
    "Enter both values (the starting value cannot be zero) to see the result.",
  increase: "increase",
  decrease: "decrease",
  noChange: "no change",
  headlinePercentOf: "{percent}% of {base} = {result}",
  headlineIsPercentOf: "{part} is {percent}% of {whole}",
} as const;

export default function PercentageCalculatorWidget() {
  const strings = useToolUi(PERCENTAGE_CALCULATOR_STRINGS);

  return (
    <>
      <PercentOfCalculator strings={strings} />
      <PercentOfWhatCalculator strings={strings} />
      <PercentChangeCalculator strings={strings} />
    </>
  );
}
